import {useOpenedStateManager} from '@aboutyou/opened-state-manager';
import {useCallback} from 'react';
import {
  SelectedSize,
  useSelectedSizeState,
} from '../SizeSelection/hooks/useSelectedSizeState';

export enum MiniAdpDialogOpenedStateKey {
  MAIN_MINI_ADP = 'wishlist-mini-adp',
  COUPON_WALLET_MAIN_MINI_ADP = 'coupon-wallet-main-mini-adp',
}

export function useMiniAdpDialogOpenedState(
  key:
    | MiniAdpDialogOpenedStateKey
    | undefined = MiniAdpDialogOpenedStateKey.MAIN_MINI_ADP,
): {
  open: ({
    productId,
    filteredSizeId,
    couponPromotionId,
    recoType,
    streamPosition,
    locationId,
  }: {
    productId: number;
    filteredSizeId?: number;
    couponPromotionId?: string;
    recoType?: string;
    streamPosition?: number;
    locationId?: string;
  }) => void;
  close: VoidFunction;
  selectedSize?: SelectedSize;
  setSelectedSize: (size: SelectedSize) => void;
  couponPromotionId?: string;
  recoType?: string;
  streamPosition?: number;
  locationId?: string;
} & (
  | {isOpen: true; productId: number; filteredSizeId?: number}
  | {isOpen: false; productId: undefined; filteredSizeId: undefined}
) {
  const {open, isOpen, cancel, parameter} = useOpenedStateManager(key);

  const [
    productIdFromParam,
    filteredSizeId,
    couponPromotionId,
    recoTypeParam,
    streamPosition,
    locationId,
  ] = (parameter || '').split(':');
  const productId = Number(productIdFromParam);
  const trackingRecoType = recoTypeParam?.replace(/@/g, ':') || undefined;
  const streamPositionNumber = streamPosition
    ? Number(streamPosition)
    : undefined;
  const {selectedSize, setSelectedSize} = useSelectedSizeState(
    undefined,
    productId,
  );

  const openDialog = useCallback(
    ({
      productId,
      filteredSizeId,
      couponPromotionId,
      recoType,
      streamPosition,
      locationId,
    }: {
      productId: number;
      filteredSizeId?: number;
      couponPromotionId?: string;
      recoType?: string;
      streamPosition?: number;
      locationId?: string;
    }) => {
      open({
        parameter: `${productId}${filteredSizeId ? `:${filteredSizeId}` : ':'}${
          couponPromotionId ? `:${couponPromotionId}` : ':'
        }${recoType ? `:${recoType.replace(':', '@')}` : ':'}${
          streamPosition ? `:${streamPosition}` : ':'
        }${locationId ? `:${locationId}` : ''}`,
      });
    },
    [open],
  );

  const closeDialog = useCallback(() => {
    setSelectedSize(undefined);
    cancel();
  }, [cancel, setSelectedSize]);

  if (!isOpen) {
    return {
      open: openDialog,
      close: closeDialog,
      selectedSize,
      setSelectedSize,
      isOpen,
      productId: undefined,
      filteredSizeId: undefined,
      recoType: trackingRecoType,
      locationId,
    };
  }

  return {
    open: openDialog,
    close: closeDialog,
    selectedSize,
    setSelectedSize,
    isOpen,
    productId,
    filteredSizeId: filteredSizeId ? Number(filteredSizeId) : undefined,
    couponPromotionId,
    recoType: trackingRecoType,
    streamPosition: streamPositionNumber,
    locationId,
  };
}
